import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { 
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import ParkingTable from '../parking/ParkingTable.jsx';
import ParkingDetailsModal from '../parking/ParkingDetailsModal.jsx';
import AddParkingZoneModal from '../parking/AddParkingZoneModal.jsx';
import ImportParkingZonesModal from '../parking/ImportParkingZonesModal.jsx';

const Parking = () => {
  const { t } = useLanguage();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedZoneDetails, setSelectedZoneDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // Fetch parking zones
  const fetchZones = async (page = 1, size = 10, query = '') => {
    try {
      setLoading(true);
      const client = getApiClient();
      const response = await client.get('/admin/parking/zones', {
        params: {
          page,
          size,
          query: query || undefined // Don't send empty query string
        }
      });
      
      console.log('Fetched parking zones:', response.data);
      
      // Handle paginated response
      if (response.data) {
        // Check if response has pagination structure
        if (response.data.content || response.data.zones) {
          const zonesData = response.data.content || response.data.zones;
          setZones(Array.isArray(zonesData) ? zonesData : []);
          setTotalPages(response.data.totalPages || 1);
          setTotalElements(response.data.totalElements || zonesData.length);
        } else if (Array.isArray(response.data)) {
          // If API returns array directly
          setZones(response.data);
          setTotalPages(1);
          setTotalElements(response.data.length);
        } else {
          setZones([]);
          setTotalPages(1);
          setTotalElements(0);
        }
      }
    } catch (error) {
      console.error('Error fetching parking zones:', error);
      setZones([]);
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones(currentPage, pageSize, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Handle search with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        fetchZones(1, pageSize, searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle view details
  const handleViewDetails = (zone) => {
    setSelectedZoneDetails(zone);
    setShowDetailsModal(true);
  };

  // Handle edit zone
  const handleEdit = (zone) => {
    setSelectedZoneDetails(zone);
    setShowAddModal(true);
  };

  // Handle delete zone
  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm(t('confirmDeleteZone'))) return;
    try {
      alert('Delete functionality will be implemented when API provides delete endpoint');
    } catch (error) {
      console.error('Error deleting zone:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      <div className="space-y-6">

        {/* Header with Add Button and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Search */}
          <div className="max-w-md flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('searchParkingZones')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center px-4 py-2 border border-purple-600 text-sm font-medium rounded-md text-purple-600 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
            >
              <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
              {t('importFromExcel')}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('addNewParkingZone')}
            </button>
          </div>
        </div>

        {/* Page Size Selector */}
        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">{t('itemsPerPage')}:</span>
            <div className="flex gap-1">
              {[10, 20, 50, 100].map((size) => (
                <button
                  key={size}
                  onClick={() => {
                    setPageSize(size);
                    setCurrentPage(1); // Reset to first page when changing size
                  }}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    pageSize === size
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          {totalElements > 0 && (
            <div className="text-sm text-gray-500">
              {t('totalZones')}: <span className="font-medium text-gray-900">{totalElements}</span>
            </div>
          )}
        </div>

        <ParkingTable 
          zones={zones} 
          onEdit={handleEdit}
          onDelete={handleDeleteZone}
          onViewDetails={handleViewDetails}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalElements={totalElements}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />

        {/* Details Modal */}
        <ParkingDetailsModal 
          zone={selectedZoneDetails}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedZoneDetails(null);
          }}
          onZoneUpdate={fetchZones}
        />

        {/* Add/Edit Zone Modal */}
        <AddParkingZoneModal 
          isOpen={showAddModal}
          zone={selectedZoneDetails}
          onClose={() => {
            setShowAddModal(false);
            setSelectedZoneDetails(null);
          }}
          onSuccess={() => {
            fetchZones(currentPage, pageSize, searchTerm);
          }}
        />

        {/* Import Modal */}
        <ImportParkingZonesModal 
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            fetchZones(currentPage, pageSize, searchTerm);
          }}
        />
      </div>
    </div>
  );
};

export default Parking;
